/**
 * EnrichFromKGModal — pull what the shared Knowledge Graph already knows about
 * this person into the caller's tree. Two sections:
 *
 *   Fields    — richer/conflicting values other researchers recorded for the
 *               same canonical person (fill an empty field, refine a vague one,
 *               or resolve a genuine conflict). Each shows provenance.
 *   Relatives — parents / partners / children other trees connect to this
 *               person but the caller's tree is missing. Importing one adds the
 *               (global) person to the tree and wires the edge in place.
 *
 * Nothing applies until the owner ticks it and hits Apply. Portaled to body so
 * it clears the Leaflet map's stacking context on the Tree page.
 */
import { useEffect, useState, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { api } from '../../services/api.js'
import './EnrichFromKGModal.css'

const FIELD_LABELS = {
  birth_date: 'Birth date', birth_place: 'Birth place',
  death_date: 'Death date', death_place: 'Death place',
  cause_of_death: 'Cause of death',
  occupation: 'Occupation', birth_name: 'Birth name', gender: 'Gender',
}
const MODE_BADGE = {
  fill:     { label: 'New', cls: 'is-fill' },
  refine:   { label: 'More detail', cls: 'is-refine' },
  conflict: { label: 'Differs', cls: 'is-conflict' },
}
const REL_LABEL = { parent: 'Parent', child: 'Child', partner: 'Partner' }

export default function EnrichFromKGModal({ personId, treeId, personName, onClose, onApplied }) {
  const [loading, setLoading] = useState(true)
  const [preview, setPreview] = useState(null)
  const [error, setError] = useState(null)

  // Selection state
  const [pickedFields, setPickedFields] = useState({})   // field -> chosen value
  const [pickedRels, setPickedRels] = useState(new Set()) // target_person_id set
  const [applying, setApplying] = useState(false)
  const [result, setResult] = useState(null)
  const [linkingId, setLinkingId] = useState(null)

  const load = useCallback(() => {
    setLoading(true)
    setError(null)
    return api.get(`/persons/${personId}/enrich-preview?tree_id=${treeId}`)
      .then(data => {
        setPreview(data)
        // Default-select safe additions (fills, refinements, all relatives);
        // leave genuine conflicts unticked so the owner makes the call.
        const f = {}
        for (const row of data.fields || []) {
          if (row.mode !== 'conflict') f[row.field] = row.proposed
        }
        setPickedFields(f)
        setPickedRels(new Set((data.relatives || []).map(r => r.target_person_id)))
      })
      .catch(e => setError(e.message || 'Could not load suggestions'))
      .finally(() => setLoading(false))
  }, [personId, treeId])

  useEffect(() => { load() }, [load])

  // Link a near-miss canonical as "the same person", then re-load so the
  // newly-reachable fields + relatives appear.
  async function linkCandidate(canonicalId) {
    setLinkingId(canonicalId)
    setError(null)
    try {
      await api.post(`/persons/${personId}/enrich-link`, {
        tree_id: treeId, target_canonical_id: canonicalId,
      })
      await load()
    } catch (e) {
      setError(e.message || 'Could not link that record')
    } finally {
      setLinkingId(null)
    }
  }

  function toggleField(row, value) {
    setPickedFields(prev => {
      const next = { ...prev }
      if (value === null) delete next[row.field]
      else next[row.field] = value
      return next
    })
  }

  function toggleRel(id) {
    setPickedRels(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  // Select-all ticks every field (conflicts included, using the currently-
  // chosen value) and every relative. Clear unticks everything.
  function selectAllItems() {
    const f = {}
    for (const row of (preview?.fields || [])) f[row.field] = pickedFields[row.field] ?? row.proposed
    setPickedFields(f)
    setPickedRels(new Set((preview?.relatives || []).map(r => r.target_person_id)))
  }
  function clearAllItems() {
    setPickedFields({})
    setPickedRels(new Set())
  }

  async function apply() {
    setApplying(true)
    setError(null)
    try {
      const relatives = (preview.relatives || [])
        .filter(r => pickedRels.has(r.target_person_id))
        .map(r => ({ target_person_id: r.target_person_id, relation: r.relation }))
      const res = await api.post(`/persons/${personId}/enrich-apply`, {
        tree_id: treeId,
        fields: pickedFields,
        relatives,
      })
      setResult(res)
      onApplied?.()
    } catch (e) {
      setError(e.message || 'Could not apply changes')
      setApplying(false)
    }
  }

  const fields = preview?.fields || []
  const relatives = preview?.relatives || []
  const candidates = preview?.candidates || []
  const nothingFound = !loading && !error
    && fields.length === 0 && relatives.length === 0 && candidates.length === 0
  const pickedCount = Object.keys(pickedFields).length + pickedRels.size

  return createPortal((
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal enrich-kg-modal" onClick={e => e.stopPropagation()}>
        <header className="modal__header">
          <h2>{result ? 'Tree updated' : 'Enrich from the Knowledge Graph'}</h2>
          <button className="modal__close" onClick={onClose} aria-label="Close">×</button>
        </header>

        <div className="enrich-kg-modal__body">
          {loading && <div className="enrich-kg-modal__muted">Checking the Knowledge Graph…</div>}

          {error && !result && <div className="enrich-kg-modal__error">{error}</div>}

          {result ? (
            <div className="enrich-kg-modal__done">
              <p className="enrich-kg-modal__intro">
                {summarise(result)}
              </p>
              <div className="enrich-kg-modal__actions">
                <button className="btn btn-primary" onClick={onClose}>Done</button>
              </div>
            </div>
          ) : nothingFound ? (
            <div className="enrich-kg-modal__muted">
              {preview?.canonical
                ? 'Nothing new to add — your tree already has everything the Knowledge Graph knows about this person.'
                : 'No Knowledge Graph match yet. Once another researcher records this same person, their data will show up here to pull in.'}
            </div>
          ) : !loading && (
            <>
              <p className="enrich-kg-modal__intro">
                What other researchers recorded about <strong>{personName}</strong> that
                your tree is missing. Tick what to keep — nothing changes until you apply.
              </p>

              {(fields.length > 0 || relatives.length > 0) && (
                <div className="enrich-kg-modal__selectall">
                  <span>{pickedCount} selected</span>
                  <button type="button" onClick={selectAllItems}>Select all</button>
                  <button type="button" onClick={clearAllItems}>Clear</button>
                </div>
              )}

              {/* ── Possible same-person matches (cross-canonical link) ────── */}
              {candidates.length > 0 && (
                <section className="enrich-kg-modal__section">
                  <div className="enrich-kg-modal__section-head">Possible same person</div>
                  <p className="enrich-kg-modal__hint">
                    Not auto-matched — a different spelling or birth year — but might be the
                    same person. Link one to pull in its details and relatives.
                  </p>
                  {candidates.map(c => (
                    <div key={c.canonical_id} className="enrich-kg-modal__row enrich-kg-modal__row--field">
                      <div className="enrich-kg-modal__row-main">
                        <div className="enrich-kg-modal__rel-line">
                          <span className="enrich-kg-modal__rel-name">{c.name}</span>
                          {c.birth_year && (
                            <span className="enrich-kg-modal__rel-years">
                              {c.birth_year}{c.death_year ? `–${c.death_year}` : ''}
                            </span>
                          )}
                        </div>
                        <div className="enrich-kg-modal__prov">
                          {c.birth_place ? `${c.birth_place} · ` : ''}
                          {c.tree_count} {c.tree_count === 1 ? 'tree' : 'trees'} · {c.name_match}% name match
                        </div>
                      </div>
                      <button
                        className="btn btn-sm btn-ghost enrich-kg-modal__link-btn"
                        disabled={linkingId === c.canonical_id}
                        onClick={() => linkCandidate(c.canonical_id)}
                      >
                        {linkingId === c.canonical_id ? 'Linking…' : 'Same person → link'}
                      </button>
                    </div>
                  ))}
                </section>
              )}

              {/* ── Relatives ─────────────────────────────────────────────── */}
              {relatives.length > 0 && (
                <section className="enrich-kg-modal__section">
                  <div className="enrich-kg-modal__section-head">
                    Relatives to add ({relatives.length})
                  </div>
                  {relatives.map(r => (
                    <label key={r.target_person_id} className="enrich-kg-modal__row">
                      <input
                        type="checkbox"
                        checked={pickedRels.has(r.target_person_id)}
                        onChange={() => toggleRel(r.target_person_id)}
                      />
                      <div className="enrich-kg-modal__row-main">
                        <div className="enrich-kg-modal__rel-line">
                          <span className={`enrich-kg-modal__rel-tag rel-${r.relation}`}>
                            {REL_LABEL[r.relation] || r.relation}
                          </span>
                          <span className="enrich-kg-modal__rel-name">{r.name || 'Unknown'}</span>
                          {r.birth_year && (
                            <span className="enrich-kg-modal__rel-years">
                              {r.birth_year}{r.death_year ? `–${r.death_year}` : ''}
                            </span>
                          )}
                        </div>
                        <div className="enrich-kg-modal__prov">
                          {r.mode === 'connect' ? 'Already in your tree — connect the link' : 'Import into your tree'}
                          {r.birth_place ? ` · ${r.birth_place}` : ''}
                          {r.source_count > 1 ? ` · in ${r.source_count} trees` : ''}
                        </div>
                      </div>
                    </label>
                  ))}
                </section>
              )}

              {/* ── Fields ────────────────────────────────────────────────── */}
              {fields.length > 0 && (
                <section className="enrich-kg-modal__section">
                  <div className="enrich-kg-modal__section-head">
                    Details to fill in ({fields.length})
                  </div>
                  {fields.map(row => {
                    const badge = MODE_BADGE[row.mode] || MODE_BADGE.fill
                    const chosen = pickedFields[row.field]
                    const checked = chosen !== undefined
                    // For conflicts, let the owner pick among the alternatives.
                    const options = [row.proposed, ...(row.alternatives || []).map(a => a.value)]
                    return (
                      <div key={row.field} className="enrich-kg-modal__row enrich-kg-modal__row--field">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleField(row, checked ? null : (chosen ?? row.proposed))}
                        />
                        <div className="enrich-kg-modal__row-main">
                          <div className="enrich-kg-modal__field-line">
                            <span className="enrich-kg-modal__field-label">{FIELD_LABELS[row.field] || row.field}</span>
                            <span className={`enrich-kg-modal__badge ${badge.cls}`}>{badge.label}</span>
                          </div>
                          {row.current && (
                            <div className="enrich-kg-modal__field-current">
                              yours: <span>{row.current}</span>
                            </div>
                          )}
                          {options.length > 1 ? (
                            <select
                              className="enrich-kg-modal__field-select"
                              value={chosen ?? row.proposed}
                              onChange={e => toggleField(row, e.target.value)}
                            >
                              {options.map((o, i) => <option key={i} value={o}>{o}</option>)}
                            </select>
                          ) : (
                            <div className="enrich-kg-modal__field-proposed">{row.proposed}</div>
                          )}
                          <div className="enrich-kg-modal__prov">{provenance(row)}</div>
                        </div>
                      </div>
                    )
                  })}
                </section>
              )}

              {preview?.canonical?.confidence && (
                <div className="enrich-kg-modal__match">
                  Match confidence: <strong>{preview.canonical.confidence}</strong>
                  {preview.canonical.contributor_count > 1
                    ? ` · ${preview.canonical.contributor_count} researchers on this person`
                    : ''}
                  . If this isn't the same person, leave it unticked.
                </div>
              )}
            </>
          )}
        </div>

        {!result && !nothingFound && !loading && (
          <footer className="enrich-kg-modal__footer">
            <button className="btn btn-ghost" onClick={onClose} disabled={applying}>Cancel</button>
            <button className="btn btn-primary" onClick={apply} disabled={applying || pickedCount === 0}>
              {applying ? 'Applying…' : `Apply ${pickedCount} ${pickedCount === 1 ? 'change' : 'changes'}`}
            </button>
          </footer>
        )}
      </div>
    </div>
  ), document.body)
}

function provenance(row) {
  const names = (row.sources || []).map(s => s.contributor_name || s.tree_name).filter(Boolean)
  if (row.source_count > 1) return `Recorded in ${row.source_count} trees`
  if (names.length) return `From ${names[0]}`
  return 'From the Knowledge Graph'
}

function summarise(r) {
  const bits = []
  if (r.relatives_added) bits.push(`${r.relatives_added} ${r.relatives_added === 1 ? 'relative' : 'relatives'} added`)
  if (r.edges_added && !r.relatives_added) bits.push(`${r.edges_added} ${r.edges_added === 1 ? 'connection' : 'connections'} linked`)
  if (r.fields_applied) bits.push(`${r.fields_applied} ${r.fields_applied === 1 ? 'detail' : 'details'} updated`)
  if (!bits.length) return 'No changes were applied.'
  let msg = bits.join(' · ') + '.'
  if (r.skipped?.some(s => s.reason === 'shared_person_requires_suggestion')) {
    msg += ' Some field changes were skipped — this person is shared across trees, so those go through suggestions instead.'
  }
  return msg
}
